/**
 * BMM — New Arrivals
 * File: bmm-new-arrivals.js
 * Version: 3.0 — Size selection + variant-aware cart
 * ──────────────────────────────────────────────────
 * Modules:
 *  1. Slider arrows (prev/next)
 *  2. Size chip selection — updates selected variant ID on cart btn
 *  3. Ajax add to cart — adds correct selected variant
 *  4. Cart tooltip — shown when cart clicked without size selected
 *  5. Cart count + Ella drawer
 *  6. Theme editor re-init
 */

(function () {
  'use strict';

  /* ── 1. SLIDER ARROWS ───────────────────────────────────────── */
  function initSlider(wrapper) {
    var slider  = wrapper.querySelector('.bmm-na__slider');
    var btnPrev = wrapper.querySelector('.bmm-na__arr--prev');
    var btnNext = wrapper.querySelector('.bmm-na__arr--next');
    if (!slider || !btnPrev || !btnNext) return;

    function getCardWidth() {
      var card = slider.querySelector('.bmm-pc');
      if (!card) return 0;
      var gap = parseInt(window.getComputedStyle(slider).gap) || 4;
      return card.offsetWidth + gap;
    }

    function updateArrows() {
      btnPrev.disabled = slider.scrollLeft <= 4;
      btnNext.disabled = slider.scrollLeft + slider.offsetWidth >= slider.scrollWidth - 4;
    }

    btnPrev.addEventListener('click', function () {
      slider.scrollBy({ left: -getCardWidth(), behavior: 'smooth' });
    });

    btnNext.addEventListener('click', function () {
      slider.scrollBy({ left: getCardWidth(), behavior: 'smooth' });
    });

    slider.addEventListener('scroll', updateArrows, { passive: true });
    updateArrows();
  }

  /* ── 2. SIZE SELECTION ──────────────────────────────────────── */
  function initSizeSelection(card) {
    var sizeButtons = card.querySelectorAll('.bmm-pc__sz[data-size]');
    var cartBtn     = card.querySelector('.bmm-pc__cart');
    if (!sizeButtons.length || !cartBtn) return;

    /* Parse variant map from data attribute */
    var variantMap = {};
    try {
      variantMap = JSON.parse(card.dataset.variants || '{}');
    } catch (e) {
      console.warn('BMM: Could not parse variant data', e);
    }

    /* Add tooltip element next to cart button */
    var tooltip = card.querySelector('.bmm-pc__cart-tooltip');
    if (!tooltip) {
      tooltip = document.createElement('span');
      tooltip.className = 'bmm-pc__cart-tooltip';
      tooltip.textContent = 'Select a size first';
      tooltip.setAttribute('aria-hidden', 'true');
      cartBtn.parentNode.insertBefore(tooltip, cartBtn.nextSibling);
    }

    /* Initial state — no size selected */
    cartBtn.dataset.variantId = '';
    cartBtn.classList.add('no-size');
    cartBtn.title = 'Select a size first';

    sizeButtons.forEach(function (btn) {
      if (btn.disabled) return; /* skip sold-out sizes */

      btn.addEventListener('click', function (e) {
        e.stopPropagation();

        var sizeValue = btn.dataset.size;

        /* Deselect all, select this one */
        sizeButtons.forEach(function (b) {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');

        /* Find matching variant */
        var match = variantMap[sizeValue];

        if (match && match.id) {
          if (match.available) {
            /* Available — arm the cart button */
            cartBtn.dataset.variantId = match.id;
            cartBtn.classList.remove('no-size', 'is-soldout');
            cartBtn.disabled = false;
            cartBtn.title = 'Add to cart';
          } else {
            /* Variant exists but sold out */
            cartBtn.dataset.variantId = '';
            cartBtn.classList.add('no-size', 'is-soldout');
            cartBtn.disabled = true;
            cartBtn.title = 'Sold out';
            tooltip.textContent = 'Sold out';
          }
        } else {
          /* No exact match (multi-option product) — go to PDP */
          cartBtn.dataset.variantId = '';
          cartBtn.classList.add('no-size');
          cartBtn.title = 'Select size on product page';
          tooltip.textContent = 'More options on product page';
        }
      });
    });
  }

  /* ── 3. AJAX ADD TO CART ────────────────────────────────────── */
  function handleCartClick(btn, card) {
    var variantId = btn.dataset.variantId;
    var title     = btn.dataset.productTitle || 'Product';
    var feedback  = btn.querySelector('.bmm-pc__cart-feedback');

    /* No size selected — show tooltip, don't fire */
    if (!variantId || variantId === '') {
      btn.classList.add('shake');
      setTimeout(function () { btn.classList.remove('shake'); }, 500);
      return;
    }

    if (btn.classList.contains('is-loading')) return;

    /* Loading state */
    btn.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');
    if (feedback) feedback.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
    .then(function (res) {
      if (!res.ok) throw new Error('Cart error ' + res.status);
      return res.json();
    })
    .then(function () {
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.setAttribute('aria-disabled', 'false');
      if (feedback) feedback.textContent = title + ' added';
      updateCartCount();
      openCartDrawer();
      setTimeout(function () {
        btn.classList.remove('is-added');
        if (feedback) feedback.textContent = '';
      }, 2500);
    })
    .catch(function (err) {
      console.warn('BMM cart error:', err);
      btn.classList.remove('is-loading');
      btn.setAttribute('aria-disabled', 'false');
      if (feedback) feedback.textContent = 'Error — try again';
      setTimeout(function () {
        if (feedback) feedback.textContent = '';
      }, 2000);
    });
  }

  /* ── 4. CART COUNT UPDATE ───────────────────────────────────── */
  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        var selectors = [
          '.cart-count', '.cart-count-bubble',
          '[data-cart-count]', '.header__cart-count',
          '.cart-link__bubble', '.site-nav__cart-count'
        ];
        selectors.forEach(function (sel) {
          document.querySelectorAll(sel).forEach(function (el) {
            el.textContent = count;
            if (count > 0) {
              el.removeAttribute('hidden');
              el.classList.remove('hide', 'hidden', 'is-hidden');
            }
          });
        });
      })
      .catch(function () {});
  }

  /* ── 5. OPEN CART DRAWER ────────────────────────────────────── */
  function openCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('theme:cart:open'));
  }

  /* ── 6. BIND ALL EVENTS ON A WRAPPER ───────────────────────── */
  function bindSection(wrapper) {
    /* Slider */
    initSlider(wrapper);

    /* Each card */
    wrapper.querySelectorAll('.bmm-pc').forEach(function (card) {
      if (card.dataset.bmmBound) return;
      card.dataset.bmmBound = '1';

      /* Size selection */
      initSizeSelection(card);

      /* Cart button */
      var cartBtn = card.querySelector('.bmm-pc__cart');
      if (cartBtn) {
        cartBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          handleCartClick(cartBtn, card);
        });
      }
    });
  }

  /* ── INIT ───────────────────────────────────────────────────── */
  function init() {
    document.querySelectorAll('.bmm-na-wrapper').forEach(function (wrapper) {
      bindSection(wrapper);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── THEME EDITOR ───────────────────────────────────────────── */
  document.addEventListener('shopify:section:load', function (e) {
    var wrapper = e.target.querySelector('.bmm-na-wrapper');
    if (wrapper) bindSection(wrapper);
  });

})();
